/**
 * EmirateViolationsSection — per-emirate service violations (same system as required docs).
 * Master violations ⋈ this service × emirate: enable per violation, edit its values locally, copy between emirates.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, RotateCcw, Save } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import EmirateDocsCopyBar from './EmirateDocsCopyBar';
import ViolationValuesFields from './ViolationValuesFields';
import { EMIRATES, normalizePricing } from './serviceEmirates';
import { emirateLabel } from './EmirateDocsSection';
import { computeFine, modeLabel } from './violationCalc';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const fetchViolationsSummary = (serviceId) =>
  axios.get(`${API_URL}/api/services/${serviceId}/violations/emirates-summary`, { headers: getAuthHeaders() })
    .then((r) => r.data?.summary || {});

export default function EmirateViolationsSection({ service, language, lockedEmirate = null, testIdPrefix = 'svc', onSummaryChange }) {
  const isAr = language === 'ar';
  const pricing = useMemo(() => normalizePricing(service?.emirate_pricing), [service]);
  const firstEnabled = pricing.dubai?.enabled ? 'dubai' : (EMIRATES.find((e) => pricing[e.key].enabled)?.key || 'dubai');
  const [emirate, setEmirate] = useState(lockedEmirate || firstEnabled);
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { if (lockedEmirate) setEmirate(lockedEmirate); }, [lockedEmirate]);

  const load = useCallback(async () => {
    if (!service?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/services/${service.id}/violations?emirate=${emirate}`, { headers: getAuthHeaders() });
      setRows(r.data?.violations || []);
      setSummary(r.data?.emirates_summary || {});
      onSummaryChange?.(r.data?.emirates_summary || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل المخالفات' : 'Failed to load violations'));
    } finally { setLoading(false); }
  }, [service?.id, emirate, reloadKey, isAr]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const applySummary = (s) => { setSummary(s); onSummaryChange?.(s); };

  const toggle = async (row, on) => {
    try {
      const r = await axios.put(`${API_URL}/api/services/${service.id}/violations/${row.id}?emirate=${emirate}`, { is_enabled: on }, { headers: getAuthHeaders() });
      setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, is_enabled: on, values: on ? (r.data?.values || x.defaults) : x.defaults, is_overridden: false } : x)));
      if (r.data?.summary) applySummary(r.data.summary);
      toast.success(isAr ? `${row.name_ar} — ${on ? 'مفعّلة' : 'غير مفعّلة'} في ${emirateLabel(emirate, true)}` : `${row.name_en || row.name_ar} — ${on ? 'enabled' : 'disabled'} in ${emirateLabel(emirate, false)}`);
    } catch (err) { toast.error(err.response?.data?.detail || (isAr ? 'فشل التحديث' : 'Update failed')); }
  };

  const saveValues = async (row, values) => {
    try {
      const r = await axios.put(`${API_URL}/api/services/${service.id}/violations/${row.id}?emirate=${emirate}`, { is_enabled: true, values }, { headers: getAuthHeaders() });
      setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, values: r.data?.values || values, is_overridden: JSON.stringify(r.data?.values) !== JSON.stringify(x.defaults) } : x)));
      toast.success(isAr ? 'تم حفظ قيم المخالفة لهذه الإمارة' : 'Violation values saved for this emirate');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(' • ') : (d || (isAr ? 'فشل الحفظ' : 'Save failed')));
    }
  };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-emirate-violations`}>
      {!lockedEmirate && (
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-violations-chips`}>
          {EMIRATES.map((e) => {
            const on = pricing[e.key].enabled; const active = emirate === e.key; const n = summary[e.key] || 0;
            return (
              <button key={e.key} type="button" onClick={() => setEmirate(e.key)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors flex items-center gap-2 ${active ? 'bg-[#c9a227] text-black border-[#c9a227]' : on ? 'bg-[#1a1a2e] text-white border-white/15 hover:border-[#c9a227]/50' : 'bg-transparent text-white/40 border-white/10 hover:text-white/70'}`}
                data-testid={`${testIdPrefix}-violations-chip-${e.key}`}>
                {emirateLabel(e.key, isAr)}
                <span className={`px-1.5 rounded-full text-[10px] ${active ? 'bg-black/20 text-black' : n ? 'bg-rose-500/20 text-rose-300' : 'bg-white/10 text-white/50'}`}>{n}</span>
                {!on && <span className="text-[10px]">{isAr ? '· غير مفعّلة' : '· off'}</span>}
              </button>
            );
          })}
        </div>
      )}

      <EmirateDocsCopyBar serviceId={service.id} emirate={emirate} summary={summary} language={language} testIdPrefix={`${testIdPrefix}-vio`}
        copyUrl={`${API_URL}/api/services/${service.id}/violations/copy`} nounAr="المخالفات" nounEn="violations" unitAr="مخالفة" unitEn="violation(s)"
        onCopied={() => setReloadKey((k) => k + 1)} />

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-rose-300" /></div>
        <div>
          <h3 className="text-lg font-bold text-white" data-testid={`${testIdPrefix}-violations-title`}>{isAr ? `مخالفات الخدمة — ${emirateLabel(emirate, true)}` : `Service violations — ${emirateLabel(emirate, false)}`}</h3>
          <p className="text-xs text-white/50">{isAr ? 'فعّل المخالفات التي تنطبق على هذه الخدمة في هذه الإمارة، وعدّل قيمها إن لزم. القيم الافتراضية من القائمة المركزية.' : 'Enable the violations that apply to this service in this emirate and adjust their values. Defaults come from the master list.'}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10 text-white/40"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-white/40 text-sm" data-testid={`${testIdPrefix}-violations-empty`}>
          {isAr ? 'لا توجد مخالفات في القائمة المركزية بعد — أضفها من: ASB606 ADMIN (للتحكم) ← قائمة مخالفات الخدمات' : 'No violations in the master list yet — add them from ASB606 ADMIN (Control) → Service Violations List'}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => <ViolationRow key={row.id} row={row} isAr={isAr} language={language} onToggle={toggle} onSave={saveValues} testIdPrefix={testIdPrefix} />)}
        </div>
      )}
    </div>
  );
}

function ViolationRow({ row, isAr, language, onToggle, onSave, testIdPrefix }) {
  const [draft, setDraft] = useState(row.values);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(row.values); }, [row.values]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(row.values);
  const v = row.values;
  return (
    <div className={`rounded-2xl border p-4 transition-colors ${row.is_enabled ? 'border-rose-500/30 bg-[#0f0f1a]' : 'border-white/10 bg-[#0a0a0a]/60'} ${!row.is_active ? 'opacity-60' : ''}`} data-testid={`${testIdPrefix}-violation-${row.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-bold ${row.is_enabled ? 'text-white' : 'text-white/60'}`}>{isAr ? row.name_ar : (row.name_en || row.name_ar)}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/60">{modeLabel(v.mode, isAr)}</span>
            {row.is_overridden && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" data-testid={`${testIdPrefix}-violation-overridden-${row.id}`}>{isAr ? 'معدّلة لهذه الإمارة' : 'customised here'}</span>}
            {!row.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">{isAr ? 'متوقفة مركزياً' : 'inactive in master'}</span>}
          </div>
          <div className="text-[11px] text-white/50 mt-1" dir="ltr">{isAr ? row.name_en : row.name_ar}</div>
          <div className="text-xs text-white/70 mt-1">
            {v.mode === 'daily' ? `${Number(v.amount || 0).toLocaleString()} ${isAr ? 'د.إ/يوم' : 'AED/day'}` : `${Number(v.amount || 0).toLocaleString()} ${isAr ? 'د.إ' : 'AED'}`}
            {v.start_fee !== null && v.start_fee !== undefined && ` · ${isAr ? 'بداية' : 'start'} ${Number(v.start_fee).toLocaleString()}`}
            {v.max_fee !== null && v.max_fee !== undefined && ` · ${isAr ? 'سقف' : 'cap'} ${Number(v.max_fee).toLocaleString()}`}
            {` · ${isAr ? 'مهلة' : 'grace'} ${Number(v.grace_days || 0)} ${isAr ? 'يوم' : 'd'}`}
            <span className="text-[#c9a227]"> · {isAr ? 'مثال 3 أيام بعد المهلة' : 'e.g. 3 days after grace'}: {computeFine(v, Number(v.grace_days || 0) + 3).toLocaleString()} {isAr ? 'د.إ' : 'AED'}</span>
          </div>
        </div>
        <Switch checked={row.is_enabled} onCheckedChange={(on) => onToggle(row, on)} data-testid={`${testIdPrefix}-violation-switch-${row.id}`} />
      </div>

      {row.is_enabled && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
          <ViolationValuesFields values={draft} onChange={setDraft} language={language} compact testIdPrefix={`${testIdPrefix}-violation-${row.id}`} />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setDraft(row.defaults)} className="h-8 border-[#2a2a4e] bg-transparent text-white text-xs" data-testid={`${testIdPrefix}-violation-reset-${row.id}`}>
              <RotateCcw className="h-3.5 w-3.5 me-1" />{isAr ? 'القيم الافتراضية' : 'Master defaults'}
            </Button>
            <Button size="sm" disabled={!dirty || saving} onClick={async () => { setSaving(true); await onSave(row, draft); setSaving(false); }} className="h-8 bg-[#d4af37] hover:bg-[#c9a227] text-black text-xs" data-testid={`${testIdPrefix}-violation-save-${row.id}`}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin me-1" /> : <Save className="h-3.5 w-3.5 me-1" />}{isAr ? 'حفظ قيم هذه الإمارة' : 'Save for this emirate'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
